<template>
  <section
    class="NotificationsPage"
    data-theme="light"
    :aria-busy="isFetching"
  >
    <ol
      v-if="crewRequests.length > 0"
      class="NotificationsPage__list"
    >
      <li
        v-for="crewRequest in crewRequests"
        :key="crewRequest.id"
        class="CrewRequestNotification"
        :data-testid="`crewRequest-${crewRequest.id}`"
      >
        <PlayerAvatar
          aria-hidden="true"
          :modelValue="crewRequest.player"
        />
        <div class="CrewRequestNotification__titleContainer">
          <h3 class="CrewRequestNotification__title">
            {{ crewRequest.player.name }}
          </h3>
          <h4 class="CrewRequestNotification__subtitle">
            {{ crewRequest.crew.name }}
          </h4>
        </div>
        <div class="CrewRequestNotification__buttonContainer">
          <button
            class="CrewRequestNotification__button"
            aria-label="Reject crew request"
            @click="handleCrewRequestReject(crewRequest)"
          >
            <Icon
              class="CrewRequestNotification__buttonIcon"
              name="x"
            />
          </button>
          <button
            class="CrewRequestNotification__button--primary CrewRequestNotification__button"
            aria-label="Accept crew request"
            @click="handleCrewRequestAccept(crewRequest)"
          >
            <Icon
              class="CrewRequestNotification__buttonIcon"
              name="check"
            />
          </button>
        </div>
      </li>
    </ol>

    <EmptyState
      v-else
      iconName="peoplePlus"
    >
      <h2>You don’t have any crew requests</h2>
      <p>
        Get out there and assemble your squad
      </p>
      <FormButton
        @click="openModal({name: RouteName.invitePlayer})"
      >
        Invite a player
      </FormButton>
    </EmptyState>
  </section>
</template>

<script lang="ts" setup>
import EmptyState from "@/components/EmptyState.vue";
import FormButton from "@/components/FormButton.vue";
import Icon from "@/components/Icon.vue";
import PlayerAvatar from "@/components/PlayerAvatar.vue";
import useVisibilityChange from "@/composables/useVisibilityChange";
import { RouteName } from "@/lib/router/routes";
import useCrewRequestStore from "@/stores/useCrewRequestStore";
import useModalStore from "@/stores/useModalStore";
import { CrewRequestWithAssociations } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { onBeforeMount, ref } from "vue";

const {
  openModal,
} = useModalStore();

const {
  crewRequests,
} = storeToRefs(useCrewRequestStore());

const {
  fetchCrewRequests,
  rejectCrewRequest,
  acceptCrewRequest,
} = useCrewRequestStore();

const isFetching = ref(false);

onBeforeMount(async () => {
  isFetching.value = true;
  try {
    await fetchCrewRequests();
  } catch (error) {
    alert(`Error fetching crew requests: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
  isFetching.value = false;
});

useVisibilityChange(handleVisibilityChange);

function handleVisibilityChange (isAppVisible: boolean) {
  if (!isAppVisible || isFetching.value) return;

  fetchCrewRequests().catch(() => {});
}

function handleCrewRequestAccept (crewRequest: CrewRequestWithAssociations) {
  try {
    acceptCrewRequest(crewRequest);
  } catch (error) {
    alert(`Error accepting crew request: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}

function handleCrewRequestReject (crewRequest: CrewRequestWithAssociations) {
  try {
    rejectCrewRequest(crewRequest);
  } catch (error) {
    alert(`Error rejecting crew request: ${error instanceof Error ? error.message : "Unknown error"}`);
  }
}
</script>

<style lang="scss" scoped>
.NotificationsPage {
  display: grid;
  height: 100%;
  background-color: var(--themeColorSurface);

  &:not(:has(.NotificationsPage__list)) {
    padding-bottom: var(--safeAreaInsetBottom);
  }
}

.NotificationsPage__list {
  display: grid;
  gap: 1px;
  align-content: start;
}

.CrewRequestNotification {
  display: grid;
  grid: auto / min-content auto min-content;
  gap: 12px;
  align-items: center;
  padding: 16px;

  outline: solid 1px var(--themeColorNeutral100);
}

.CrewRequestNotification__titleContainer {
  display: grid;
  gap: 4px;
  margin-top: var(--fontSafariOffset);
}

.CrewRequestNotification__title {
  overflow: hidden;

  font: var(--fontWeightNormal) 20px / 1 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.CrewRequestNotification__subtitle {
  overflow: hidden;

  color: var(--themeColorNeutral600);

  font: var(--fontWeightNormal) 16px / 1 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.CrewRequestNotification__buttonContainer {
  --buttonPadding: 8px;
  display: flex;
  margin-inline: calc(-1 * (2px + var(--buttonPadding)));
}

.CrewRequestNotification__button {
  --size: 28px;
  position: relative;
  isolation: isolate;

  width: calc(var(--size) + 2 * var(--buttonPadding));
  aspect-ratio: 1;
  padding: var(--buttonPadding);

  &::before {
    position: absolute;
    top: calc(50% - var(--size) / 2);
    left: calc(50% - var(--size) / 2);

    width: var(--size);
    aspect-ratio: 1;

    border: solid 2px var(--themeColorNeutral900);
    border-radius: 50%;
    background-color: var(--themeColorNeutral100);

    content: "";
  }
}

.CrewRequestNotification__button--primary::before {
  background-color: var(--themeColorSecondary);
}

.CrewRequestNotification__buttonIcon {
  --iconSize: 14px;
  position: absolute;
  top: calc(50% - var(--iconSize) / 2);
  left: calc(50% - var(--iconSize) / 2);

  color: var(--themeColorSecondaryForeground);
}
</style>
